import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useApp } from '@/contexts/AppContext';
import { t as tRaw } from '@/locales/strings';
import { hapticTap, hapticSuccess, hapticWarn } from '@/utils/haptics';
import BrandLogo from '@/components/ui/BrandLogo';

// ── React replacement for the vanilla s-login screen ──────────────────
//
// Three modes: 'login' (sign in), 'signup' (new account), 'recover'
// (enter new password after clicking the reset-email link). The
// recover mode auto-activates when AppContext reports the Supabase
// PASSWORD_RECOVERY event. A fourth transient mode 'forgot' just
// collects the email and calls resetPasswordForEmail.
//
// Why this exists: the old s-login screen is vanilla DOM in
// index.html + imperative handlers in src/main.js. This component
// replaces it outright — when AppShell sees !user (or passwordRecovery
// true), it renders <AuthScreen /> and hides the vanilla markup via
// the effect that strips .on off the legacy screens. Everything the
// user sees on the way into the app (language toggle, login, signup,
// forgot, recover) now lives in React.

const s = {
  screen: {
    position: 'fixed',
    inset: 0,
    zIndex: 9998,
    // Soft radial wash anchored top-centre so the screen feels lit
    // around the wordmark instead of a flat cream field.  Pure rose
    // tint at 40% over the cream base.
    background: 'radial-gradient(120% 70% at 50% 0%, color-mix(in srgb, var(--primary-pale, #F5D6E0) 38%, transparent) 0%, var(--cream, #FAF6F1) 60%)',
    display: 'flex',
    flexDirection: 'column',
    padding: 'env(safe-area-inset-top, 0px) 0 env(safe-area-inset-bottom, 0px)',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '12px 20px 0',
  },
  langToggle: {
    display: 'inline-flex',
    gap: 10,
    alignItems: 'center',
  },
  flagBtn: {
    width: 34,
    height: 34,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    background: 'transparent',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    opacity: 0.45,
    transition: 'opacity 150ms ease, box-shadow 150ms ease',
    boxShadow: '0 0 0 1px rgba(90,62,43,0.15)',
  },
  flagBtnActive: {
    opacity: 1,
    boxShadow: '0 0 0 2px var(--primary, #A8003F)',
  },
  body: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 24px 40px',
  },
  card: {
    width: '100%',
    maxWidth: 400,
  },
  wordmark: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 28,
  },
  eyebrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.24em',
    textTransform: 'uppercase',
    color: 'var(--primary-dark, var(--primary, #A8003F))',
    margin: '0 0 10px',
  },
  eyebrowDot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'var(--primary, #A8003F)',
  },
  title: {
    fontFamily: "var(--font-title, 'Playfair Display', Georgia, serif)",
    fontSize: 32,
    fontWeight: 500,
    fontStyle: 'italic',
    color: 'var(--dark-brown, #5A3E2B)',
    lineHeight: 1.12,
    letterSpacing: '-0.018em',
    margin: '0 0 10px',
    textAlign: 'center',
    fontVariationSettings: '"opsz" 96',
    textWrap: 'balance',
  },
  sub: {
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 15,
    color: 'var(--warm-brown, #8B7355)',
    lineHeight: 1.5,
    margin: '0 0 28px',
    textAlign: 'center',
    textWrap: 'balance',
  },
  field: {
    width: '100%',
    padding: '15px 16px',
    marginBottom: 12,
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 'var(--radius-md, 12px)',
    background: '#fff',
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 16,
    color: 'var(--dark-brown, #5A3E2B)',
    outline: 'none',
    WebkitAppearance: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
    boxShadow: '0 1px 2px rgba(45,26,31,0.03)',
  },
  primary: {
    width: '100%',
    minHeight: 52,
    padding: '14px 20px',
    marginTop: 8,
    border: 'none',
    borderRadius: 999,
    background: 'linear-gradient(180deg, #B8094A 0%, var(--primary, #A8003F) 100%)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22), 0 1px 2px rgba(45,26,31,0.06), 0 14px 28px -10px rgba(168,0,63,0.45)',
    color: '#fff',
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.10em',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'opacity 150ms ease',
  },
  primaryBusy: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  link: {
    display: 'block',
    width: '100%',
    padding: '12px 0',
    marginTop: 6,
    background: 'transparent',
    border: 'none',
    textAlign: 'center',
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--warm-brown, #8B7355)',
    textDecoration: 'underline',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  error: {
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 13,
    lineHeight: 1.45,
    color: '#B91C1C',
    margin: '4px 0 12px',
    minHeight: 18,
    textAlign: 'center',
  },
  ok: {
    color: '#15803D',
  },
};

// Inline SVG circle flags for the EN / PT toggle. Matches the AU + BR
// convention used by the legacy /s-lang screen (index.html), just in
// SVG form so rendering stays consistent across OSes (emoji flags
// render as letter pairs on Windows).
function FlagAU({ size = 28 }) {
  return (
    <svg viewBox="0 0 60 60" width={size} height={size} style={{ display: 'block' }} aria-hidden="true">
      <defs>
        <clipPath id="bb-flag-au-clip"><circle cx="30" cy="30" r="30" /></clipPath>
      </defs>
      <g clipPath="url(#bb-flag-au-clip)">
        <rect width="60" height="60" fill="#00247D" />
        <g>
          <path d="M0,0 L30,30 M30,0 L0,30" stroke="#fff" strokeWidth="6" />
          <path d="M0,0 L30,30 M30,0 L0,30" stroke="#CF142B" strokeWidth="3" />
          <path d="M15,0 L15,30 M0,15 L30,15" stroke="#fff" strokeWidth="9" />
          <path d="M15,0 L15,30 M0,15 L30,15" stroke="#CF142B" strokeWidth="5" />
        </g>
        <circle cx="15" cy="46" r="3" fill="#fff" />
        <circle cx="42" cy="14" r="2.2" fill="#fff" />
        <circle cx="50" cy="24" r="2.2" fill="#fff" />
        <circle cx="40" cy="36" r="2.2" fill="#fff" />
        <circle cx="48" cy="46" r="2.2" fill="#fff" />
        <circle cx="53" cy="34" r="1.4" fill="#fff" />
      </g>
    </svg>
  );
}

function FlagBR({ size = 28 }) {
  return (
    <svg viewBox="0 0 60 60" width={size} height={size} style={{ display: 'block' }} aria-hidden="true">
      <defs>
        <clipPath id="bb-flag-br-clip"><circle cx="30" cy="30" r="30" /></clipPath>
      </defs>
      <g clipPath="url(#bb-flag-br-clip)">
        <rect width="60" height="60" fill="#009C3B" />
        <polygon points="30,10 52,30 30,50 8,30" fill="#FFDF00" />
        <circle cx="30" cy="30" r="10" fill="#002776" />
        <path d="M21,29 Q30,24 39,29" stroke="#fff" strokeWidth="1.2" fill="none" />
      </g>
    </svg>
  );
}

// Map common Supabase auth error messages to short friendly copy.
// Falls back to the raw message for anything we don't recognise so
// support debugging never loses the server-side signal.
function friendlyError(err, lang) {
  if (!err) return '';
  const msg = (err.message || String(err)).toLowerCase();
  if (msg.includes('invalid login')) return tRaw('auth_err_invalid_login', lang);
  if (msg.includes('email not confirmed')) return tRaw('auth_err_email_unconfirmed', lang);
  if (msg.includes('already registered')) return tRaw('auth_err_already_registered', lang);
  if (msg.includes('password should be') || msg.includes('password is too short'))
    return tRaw('auth_err_password_short', lang);
  if (msg.includes('rate limit') || msg.includes('too many'))
    return tRaw('auth_err_rate_limit', lang);
  if (msg.includes('network') || msg.includes('fetch')) return tRaw('auth_err_network', lang);
  return err.message || tRaw('auth_err_generic', lang);
}

export default function AuthScreen() {
  const { lang, setLang, passwordRecovery, setPasswordRecovery } = useApp();

  // Mode: 'login' | 'signup' | 'forgot' | 'recover'. passwordRecovery
  // from context pins us to 'recover' until the user either completes
  // the flow or explicitly cancels out of it.
  const [mode, setMode] = useState(passwordRecovery ? 'recover' : 'login');
  useEffect(() => {
    if (passwordRecovery) setMode('recover');
  }, [passwordRecovery]);

  // Strip every vanilla .screen that might still be `.on` underneath
  // us. Broad sweep is safer than an id allowlist — when AuthScreen
  // renders, no vanilla screen should paint. Also force-hide the
  // bootstrap app-loading overlay, which main.js turns on during
  // auth transitions and can linger after a failed vanilla login.
  useEffect(() => {
    document.querySelectorAll('.screen.on').forEach((el) => el.classList.remove('on'));
    const loader = document.getElementById('app-loading');
    if (loader) loader.style.display = 'none';
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  // Tracks mount status so async handlers (signInWithPassword,
  // updateUser, resetPasswordForEmail) can bail before calling
  // setState on a component that AppShell has already unmounted.
  // Supabase flips the auth state synchronously after a successful
  // sign-in, which causes AppShell to render AppRoutes and unmount
  // AuthScreen before our own `setBusy(false)` fires. Not a crash —
  // React tolerates it — but the warning is noise and any further
  // setState is dead work.
  const mounted = useRef(true);
  // In-flight mutex for auth calls. `busy` is a state flag for the
  // UI; this ref is the authoritative gate — React batches state
  // updates, so `if (busy) return;` on rapid double-taps of the
  // submit button can race (both reads see busy=false on the same
  // microtask). The ref flips synchronously and is the final say
  // on whether another Supabase call is allowed through.
  const authInFlight = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Collect all pending timers so the unmount path can clear them.
  // Currently only the post-recover 800ms transition uses one, but
  // centralising makes future additions safe by default.
  const timersRef = useRef(new Set());
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);
  function scheduleTimeout(fn, ms) {
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      if (mounted.current) fn();
    }, ms);
    timersRef.current.add(id);
    return id;
  }

  // Focus discipline: on every mode change, move focus to the heading
  // so screen-reader users hear the new screen title. We do NOT
  // auto-focus the email / password input — that fought the heading
  // focus on mount and left keyboard users guessing where focus was.
  // Keyboard users reach the first input with one Tab; the headings
  // are tabindex=-1 so they're skipped in the normal Tab order after
  // the initial landing.
  const headingRef = useRef(null);
  useEffect(() => {
    if (!headingRef.current) return;
    headingRef.current.setAttribute('tabindex', '-1');
    try { headingRef.current.focus({ preventScroll: true }); } catch {}
  }, [mode]);

  function handleLangChange(next) {
    if (next === lang) return;
    setLang(next);
    try {
      const saved = localStorage.getItem('bb7');
      const data = saved ? JSON.parse(saved) : {};
      data.lang = next;
      localStorage.setItem('bb7', JSON.stringify(data));
      localStorage.setItem('bebold_language', next);
    } catch {}
  }

  function resetFeedback() { setErr(''); setInfo(''); }

  async function handleSignIn(e) {
    e?.preventDefault?.();
    if (authInFlight.current) return;
    resetFeedback();
    if (!supabase) { setErr(tRaw('auth_err_service_unavailable', lang)); return; }
    if (!email.trim() || !password) { setErr(tRaw('auth_err_missing_fields', lang)); return; }
    hapticTap();
    authInFlight.current = true;
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      // Success path: AppContext.onAuthStateChange fires SIGNED_IN,
      // AppShell unmounts AuthScreen. `mounted.current` guards the
      // state updates below so we don't warn on the way out.
      if (!mounted.current) return;
      setBusy(false);
      if (error) {
        setErr(friendlyError(error, lang));
        hapticWarn();
        return;
      }
      hapticSuccess();
    } finally {
      authInFlight.current = false;
    }
  }

  async function handleSignUp(e) {
    e?.preventDefault?.();
    if (authInFlight.current) return;
    resetFeedback();
    if (!supabase) { setErr(tRaw('auth_err_service_unavailable', lang)); return; }
    if (!email.trim() || !password) { setErr(tRaw('auth_err_missing_fields', lang)); return; }
    if (password.length < 6) { setErr(tRaw('auth_err_password_short', lang)); return; }
    hapticTap();
    authInFlight.current = true;
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
      });
      if (!mounted.current) return;
      setBusy(false);
      if (error) {
        setErr(friendlyError(error, lang));
        hapticWarn();
        return;
      }
      // Supabase email-confirmation flow: data.user exists but no
      // data.session means the user must click the confirmation email
      // before the session materialises. Tell them so.
      if (data?.user && !data.session) {
        setInfo(tRaw('auth_check_email_confirm', lang));
        hapticSuccess();
        return;
      }
      hapticSuccess();
    } finally {
      authInFlight.current = false;
    }
  }

  async function handleForgot(e) {
    e?.preventDefault?.();
    if (authInFlight.current) return;
    resetFeedback();
    if (!supabase) { setErr(tRaw('auth_err_service_unavailable', lang)); return; }
    if (!email.trim()) { setErr(tRaw('auth_err_enter_email', lang)); return; }
    hapticTap();
    authInFlight.current = true;
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin,
      });
      if (!mounted.current) return;
      setBusy(false);
      if (error) { setErr(friendlyError(error, lang)); hapticWarn(); return; }
      setInfo(tRaw('auth_reset_link_sent', lang));
      hapticSuccess();
    } finally {
      authInFlight.current = false;
    }
  }

  async function handleRecover(e) {
    e?.preventDefault?.();
    if (busy) return;
    resetFeedback();
    if (!supabase) { setErr(tRaw('auth_err_service_unavailable', lang)); return; }
    if (!newPassword || newPassword.length < 6) {
      setErr(tRaw('auth_err_password_short', lang));
      return;
    }
    hapticTap();
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (!mounted.current) return;
    setBusy(false);
    if (error) { setErr(friendlyError(error, lang)); hapticWarn(); return; }
    // Supabase has already set the session via the recovery link, so
    // there is no "sign-in" step to run — just clear the recovery
    // flag and let AppShell proceed. scheduleTimeout self-clears on
    // unmount so a fast user who navigates away during the 800ms
    // pause doesn't get a state update on a dead component.
    setInfo(tRaw('auth_password_updated', lang));
    hapticSuccess();
    scheduleTimeout(() => { setPasswordRecovery(false); setMode('login'); }, 800);
  }

  const titleKey =
    mode === 'signup' ? 'auth_signup_title'
    : mode === 'forgot' ? 'auth_forgot_title'
    : mode === 'recover' ? 'auth_recover_title'
    : 'auth_login_title';
  const subKey =
    mode === 'signup' ? 'auth_signup_sub'
    : mode === 'forgot' ? 'auth_forgot_sub'
    : mode === 'recover' ? 'auth_recover_sub'
    : 'auth_login_sub';
  const submitKey =
    mode === 'signup' ? 'auth_signup_btn'
    : mode === 'forgot' ? 'auth_forgot_btn'
    : mode === 'recover' ? 'auth_recover_btn'
    : 'auth_login_btn';
  const onSubmit =
    mode === 'signup' ? handleSignUp
    : mode === 'forgot' ? handleForgot
    : mode === 'recover' ? handleRecover
    : handleSignIn;

  return (
    <div style={s.screen}>
      <header style={s.header}>
        <div style={s.langToggle} role="group" aria-label="Language">
          <button
            type="button"
            style={{ ...s.flagBtn, ...(lang === 'en' ? s.flagBtnActive : null) }}
            onClick={() => handleLangChange('en')}
            aria-pressed={lang === 'en'}
            aria-label="English"
            title="English"
          >
            <FlagAU size={34} />
          </button>
          <button
            type="button"
            style={{ ...s.flagBtn, ...(lang === 'pt' ? s.flagBtnActive : null) }}
            onClick={() => handleLangChange('pt')}
            aria-pressed={lang === 'pt'}
            aria-label="Português"
            title="Português"
          >
            <FlagBR size={34} />
          </button>
        </div>
      </header>

      <div style={s.body}>
        <form style={s.card} onSubmit={onSubmit} noValidate>
          <div style={s.wordmark} role="banner" aria-label="Be Bold">
            <BrandLogo size={176} />
          </div>
          {/* Editorial eyebrow above the title — sets the tone of the
              screen (welcome / new account / reset) and matches the
              eyebrow pattern used on Method, Learning, and Membership. */}
          <div style={s.eyebrow}>
            <span aria-hidden="true" style={s.eyebrowDot} />
            {tRaw(
              mode === 'signup' ? 'auth_eyebrow_signup'
              : mode === 'forgot' ? 'auth_eyebrow_forgot'
              : mode === 'recover' ? 'auth_eyebrow_recover'
              : 'auth_eyebrow_login',
              lang,
            )}
          </div>
          <h1 ref={headingRef} style={s.title}>{tRaw(titleKey, lang)}</h1>
          <p style={s.sub}>{tRaw(subKey, lang)}</p>

          {err && <div role="alert" style={s.error}>{err}</div>}
          {info && <div role="status" style={{ ...s.error, ...s.ok }}>{info}</div>}

          {mode !== 'recover' && (
            <>
              <label htmlFor="auth-email" className="sr-only">Email</label>
              <input
                id="auth-email"
                style={s.field}
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder={tRaw('auth_email_placeholder', lang)}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
            </>
          )}

          {(mode === 'login' || mode === 'signup') && (
            <>
              <label htmlFor="auth-password" className="sr-only">Password</label>
              <input
                id="auth-password"
                style={s.field}
                type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                placeholder={tRaw('auth_password_placeholder', lang)}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </>
          )}

          {mode === 'recover' && (
            <>
              <label htmlFor="auth-new-password" className="sr-only">New password</label>
              <input
                id="auth-new-password"
                style={s.field}
                type="password"
                autoComplete="new-password"
                placeholder={tRaw('auth_new_password_placeholder', lang)}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={busy}
              />
            </>
          )}

          <button
            type="submit"
            style={{ ...s.primary, ...(busy ? s.primaryBusy : null) }}
            disabled={busy}
            aria-busy={busy}
          >
            {busy ? tRaw('auth_one_moment', lang) : tRaw(submitKey, lang)}
          </button>

          {mode === 'login' && (
            <>
              <button
                type="button"
                style={s.link}
                onClick={() => { resetFeedback(); setMode('signup'); }}
                disabled={busy}
              >
                {tRaw('auth_to_signup', lang)}
              </button>
              <button
                type="button"
                style={s.link}
                onClick={() => { resetFeedback(); setMode('forgot'); }}
                disabled={busy}
              >
                {tRaw('auth_forgot_link', lang)}
              </button>
            </>
          )}

          {mode === 'signup' && (
            <button
              type="button"
              style={s.link}
              onClick={() => { resetFeedback(); setMode('login'); }}
              disabled={busy}
            >
              {tRaw('auth_to_login', lang)}
            </button>
          )}

          {mode === 'forgot' && (
            <button
              type="button"
              style={s.link}
              onClick={() => { resetFeedback(); setMode('login'); }}
              disabled={busy}
            >
              {tRaw('auth_back_to_login', lang)}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
